<template>
  <div class="home detail-actions-page">
    <div class="px-8 py-4">
      <!-- Header -->
      <div class="invoice-details-header mb-4">
        <button type="button" class="btn invoice-back-button" :aria-label="$t('back')" @click="$router.go(-1)">
          <AppIcon name="bi-arrow-left" class="bi bi-arrow-left" aria-hidden="true" />
          <span>{{$t('back')}}</span>
        </button>
        <div class="invoice-title-row d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">
          <h3 class="invoice-reference mb-0">{{ order.reference }}</h3>
          <div class="invoice-actions" role="toolbar" :aria-label="$t('actions')">
            <button
              v-if="order.status != 'draft'"
              type="button"
              class="btn invoice-action-button invoice-action-button--primary"
              @click="ViewOrder()"
            >
              <AppIcon name="bi-eye" class="bi bi-eye" aria-hidden="true" />
              <span>{{$t('view')}}</span>
            </button>
            <button
              v-if="helper.checkAccess('edit_orders') != -1"
              type="button"
              class="btn invoice-action-button invoice-action-button--secondary"
              @click="editRow()"
            >
              <AppIcon name="bi-pencil" class="bi bi-pencil" aria-hidden="true" />
              <span>{{$t('edit')}}</span>
            </button>
            <div
              v-if="order.status != 'draft' && (helper.checkAccess('create_deliveries') > 0 || helper.checkAccess('create_invoices') > 0)"
              class="btn-group invoice-action-group"
            >
              <button
                id="orderTransferButton"
                class="btn invoice-action-button invoice-action-button--secondary dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                <AppIcon name="bi-arrow-left-right" class="bi bi-arrow-left-right" aria-hidden="true" />
                <span>{{$t('transfert')}}</span>
              </button>
              <ul class="dropdown-menu dropdown-menu-end invoice-actions-menu" aria-labelledby="orderTransferButton">
                <li v-if="helper.checkAccess('create_deliveries') > 0">
                  <button type="button" class="dropdown-item" @click="transferDelivery()">
                    <AppIcon name="bi-truck" class="bi bi-truck" aria-hidden="true" />
                    <span>{{$t('delivery')}}</span>
                  </button>
                </li>
                <li v-if="helper.checkAccess('create_invoices') > 0">
                  <button type="button" class="dropdown-item" @click="transferInvoice()">
                    <AppIcon name="bi-receipt" class="bi bi-receipt" aria-hidden="true" />
                    <span>{{$t('invoice')}}</span>
                  </button>
                </li>
              </ul>
            </div>
            <div v-if="order.status != 'draft' || helper.checkAccess('delete_orders') != -1" class="btn-group invoice-action-group">
              <button
                id="orderMoreActionsButton"
                class="btn invoice-action-button invoice-action-button--secondary invoice-action-button--icon dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                :aria-label="$t('more')"
                :title="$t('more')"
              >
                <AppIcon name="bi-three-dots" class="bi bi-three-dots" aria-hidden="true" />
              </button>
              <ul class="dropdown-menu dropdown-menu-end invoice-actions-menu" aria-labelledby="orderMoreActionsButton">
                <li v-if="order.status != 'draft'">
                  <button type="button" class="dropdown-item" @click="toggleReorder">
                    <AppIcon name="bi-arrow-down-up" class="bi bi-arrow-down-up" aria-hidden="true" />
                    <span>{{$t('reorder')}}</span>
                  </button>
                </li>
                <li v-if="order.status != 'draft' && helper.checkAccess('delete_orders') != -1"><hr class="dropdown-divider"></li>
                <li v-if="helper.checkAccess('delete_orders') != -1">
                  <button type="button" class="dropdown-item invoice-menu-action--danger" @click="deleteRow()">
                    <AppIcon name="bi-trash" class="bi bi-trash" aria-hidden="true" />
                    <span>{{$t('delete')}}</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center" style="padding:2rem">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Order Info -->
      <div v-else-if="order && order.id">
        <!-- Tabs -->
        <ul class="nav nav-tabs detail-page-tabs mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'overview'}" @click="step = 'overview'">{{$t('overview')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_quotes') != -1">
            <a class="nav-link" :class="{active: step === 'quote'}" @click="step = 'quote'">{{$t('quote')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_deliveries') != -1">
            <a class="nav-link" :class="{active: step === 'deliveries'}" @click="step = 'deliveries'">{{$t('deliveries')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_invoices') != -1">
            <a class="nav-link" :class="{active: step === 'invoices'}" @click="step = 'invoices'">{{$t('invoices')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_returns') != -1">
            <a class="nav-link" :class="{active: step === 'returns'}" @click="step = 'returns'">{{$t('returns')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_attachments') != -1">
            <a class="nav-link" :class="{active: step === 'attachments'}" @click="step = 'attachments'">{{$t('attachments')}}</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'history'}" @click="step = 'history'">{{$t('history')}}</a>
          </li>
        </ul>

        <!-- Tab Content -->
        <!-- Overview Tab -->
        <div v-if="step === 'overview'">
          <div class="row document-overview-grid">
            <!-- Left Column: Order Details -->
            <div class="col-md-8 document-overview-main">
              <div class="card mb-4 document-pdf-card">
                <div class="card-body p-0">
                  <invoice-template-modal v-if="order && order.id" inline :data="order" type="order" />
                </div>
              </div>
              <!-- Products Card -->
              <div v-if="reorderMode" class="card mb-4 document-products-card">
                <div class="card-header bg-white">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0"><AppIcon name="bi-box-seam" class="bi bi-box-seam me-2" />{{$t('products')}}</h6>
                    <div class="product-reorder-actions">
                        <div v-if="reorderMode" class="d-flex flex-wrap gap-2" role="group" :aria-label="$t('reorder')">
                            <button type="button" class="btn product-action-button product-action-button--save" @click="saveOrder">
                                <AppIcon name="bi-check-lg" class="bi bi-check-lg" aria-hidden="true" />
                                {{$t('save')}}
                            </button>
                            <button type="button" class="btn product-action-button" @click="toggleReorder">
                                <AppIcon name="bi-x-lg" class="bi bi-x-lg" aria-hidden="true" />
                                {{$t('cancel')}}
                            </button>
                        </div>
                    </div>
                  </div>
                </div>
                <div class="card-body table-responsive">
                  <table class="table mb-0 table-striped small">
                    <thead>
                      <tr class="text-secondary">
                        <th>{{$t('product_label')}}</th>
                        <th>{{$t('purchase_price')}}</th>
                        <th>{{$t('price')}}</th>
                        <th>{{$t('quantity')}}</th>
                        <th>{{$t('vat')}}</th>
                        <th>{{$t('sub_total')}}</th>
                        <th>{{$t('total')}}</th>
                        <th v-if="reorderMode" style="width: 100px;">{{$t('actions')}}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(product, index) in order.products" :key="product.id">
                        <td><div class="product-rich-text" v-html="sanitizeRichText(product.product_label)"></div></td>
                        <td>{{formatMoney(product.purchase_price)}}</td>
                        <td>{{formatMoney(product.price)}}</td>
                        <td>{{product.quantity}}</td>
                        <td>{{product.vat}} %</td>
                        <td>{{formatMoney(product.sub_total)}}</td>
                        <td>{{formatMoney(product.total)}}</td>
                        <td v-if="reorderMode">
                            <div class="d-flex gap-1">
                                <button
                                  type="button"
                                  class="btn product-move-button"
                                  :aria-label="$t('reorder') + ' ↑'"
                                  :title="$t('reorder') + ' ↑'"
                                  :disabled="index === 0"
                                  @click="moveUp(index)"
                                >
                                  <AppIcon name="bi-arrow-up" class="bi bi-arrow-up" aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  class="btn product-move-button"
                                  :aria-label="$t('reorder') + ' ↓'"
                                  :title="$t('reorder') + ' ↓'"
                                  :disabled="index === order.products.length - 1"
                                  @click="moveDown(index)"
                                >
                                  <AppIcon name="bi-arrow-down" class="bi bi-arrow-down" aria-hidden="true" />
                                </button>
                            </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Notes Card -->
              <div v-if="order.notes" class="card document-notes-card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-sticky" class="bi bi-sticky me-2" />{{$t('notes')}}</h6>
                </div>
                <div class="card-body">
                  <p class="mb-0">{{ order.notes }}</p>
                </div>
              </div>
            </div>

            <!-- Right Column: Client & Totals Info -->
            <div class="col-md-4 document-overview-sidebar">
              <div class="card mb-4 document-info-card">
                <div class="card-header bg-white"><h6 class="mb-0"><AppIcon name="bi-cart" class="bi bi-cart me-2" />{{$t('order')}} {{$t('information')}}</h6></div>
                <div class="card-body"><div class="row">
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('reference')}}</small><strong>{{order.reference}}</strong></div>
                  <div class="col-6 mb-3">
                    <small class="text-muted d-block">{{$t('status')}}</small>
                    <div v-if="helper.checkAccess('edit_orders') != -1 && !['draft', 'canceled'].includes(order.status)" class="dropdown">
                      <button id="order-details-status-sidebar" type="button" class="badge border-0 dropdown-toggle" :class="getOrderStatusBadgeClass(order.status)" data-bs-toggle="dropdown" aria-expanded="false" :disabled="isOrderStatusDropdownDisabled(order)"><span v-if="orderStatusUpdatingId === order.id" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>{{ $t(order.status) }}</button>
                      <ul class="dropdown-menu shadow" aria-labelledby="order-details-status-sidebar"><li v-for="status in getOrderStatusOptions(order)" :key="status"><button type="button" class="dropdown-item d-flex align-items-center justify-content-between gap-3" :class="{ active: order.status === status }" :disabled="order.status === status" @click="changeOrderStatus(order, status)"><span>{{ $t(status) }}</span><AppIcon name="bi-check-lg" v-if="order.status === status" class="bi bi-check-lg" /></button></li></ul>
                    </div>
                    <span v-else class="badge" :class="getOrderStatusBadgeClass(order.status)">{{ $t(order.status) }}</span>
                  </div>
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('order_date')}}</small><strong>{{formatDate(order.date)}}</strong></div>
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('created_by')}}</small><strong>{{order.user ? order.user.name : '-'}}</strong></div>
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('created_at')}}</small><strong>{{formatDate(order.created_at)}}</strong></div>
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('updated_at')}}</small><strong>{{formatDate(order.updated_at)}}</strong></div>
                </div></div>
              </div>
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-person" class="bi bi-person me-2" />{{$t('client')}}</h6>
                </div>
                <div class="card-body">
                  <div v-if="!order.client" class="text-muted">{{$t('no_client_found')}}</div>
                  <div v-else>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('reference')}}</small>
                      <strong>{{order?.client?.reference}}</strong>
                    </div>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('name')}}</small>
                      <strong>{{order?.client?.name}}</strong>
                    </div>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('phone')}}</small>
                      <strong>{{order?.client?.phone || '-'}}</strong>
                    </div>
                    <div>
                      <small class="text-muted d-block">{{$t('email')}}</small>
                      <strong>{{order?.client?.email || '-'}}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tot  als Card -->
              <div class="card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-calculator" class="bi bi-calculator me-2" />{{$t('totals')}}</h6>
                </div>
                <div class="card-body">
                  <div class="mb-3">
                    <small class="text-muted d-block">{{$t('sub_total')}}</small>
                    <h5 class="mb-0 text-muted">{{formatMoney(order.sub_total)}}</h5>
                  </div>
                  <div class="mb-3" v-if="order.total_discount > 0">
                    <small class="text-muted d-block">{{$t('total_discount')}}</small>
                    <h5 class="mb-0 text-muted">-{{formatMoney(order.total_discount)}}</h5>
                  </div>
                  <div class="mb-3">
                    <small class="text-muted d-block">{{$t('total_tax')}}</small>
                    <h5 class="mb-0 text-muted">{{formatMoney(order.total_vat)}}</h5>
                  </div>  
                  <div>
                    <small class="text-muted d-block">{{$t('total')}}</small>
                    <h5 class="text mb-0">{{formatMoney(order.total)}}</h5>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- Other Tabs -->
        <div v-if="step === 'quote' && helper.checkAccess('view_quotes') != -1">
          <quote-component v-if="order && order.id" :products="order.products" :order_id="this.order.id" :client="order.client" :hideAction="true" :isDetailClient="true" :quote_id="order.quote_id"></quote-component>
        </div>

        <div v-if="step === 'deliveries' && helper.checkAccess('view_deliveries') != -1">
          <delivery-component v-if="order && order.id" :products="order.products" :order_reference="order.reference" :client="order.client" :order_id="this.order.id" :isDetailClient="true"></delivery-component>
        </div>

        <div v-if="step === 'invoices' && helper.checkAccess('view_invoices') != -1">
          <invoice-component v-if="order && order.id" :products="order.products" :client="order.client" :order_id="this.order.id" :isDetailClient="true"></invoice-component>
        </div>

        <div v-if="step === 'returns' && helper.checkAccess('view_returns') != -1">
          <return-component v-if="order && order.id" :products="order.products" :client="order.client" :order_id="this.order.id" :isDetailClient="true"></return-component>
        </div>

        <div v-if="step === 'attachments' && helper.checkAccess('view_attachments') != -1">
          <attachments :entity_id="order.id" entity_type="order" />
        </div>

        <div v-if="step === 'history'">
          <document-history
            :history="history"
            :loading="loadingHistory"
            :pagination="historyPagination"
            @pageChange="changeHistoryPage"
          />
        </div>
      </div>
    </div>

        <!-- Edit Modal -->
        <add-edit-order-modal 
            v-if="editModalisOpen" 
            @modalClosed="editModalisOpen = false" 
            modalId="edit-order-modal-02" 
            :isModalOpen="editModalisOpen"  
            :data="order" 
            action="update" 
        ></add-edit-order-modal>
        <!-- Edit modal -->

        <!-- quote modal -->
        <invoice-template-modal 
            v-if="invoiceModalisOpen" 
            @modalClosed="invoiceModalisOpen = false" 
            modalId="invoice-order-modal-02" 
            :isModalOpen="invoiceModalisOpen"  
            :data="order" 
            type="order">
        </invoice-template-modal>
        <!-- quote modal -->


        <!-- create Modal -->
        <add-edit-invoice-modal 
            v-if="createInvoiceModalState" 
            @modalClosed="createInvoiceModalState = false"
            modalId="create-invoice-modal-01" 
            :isModalOpen="createInvoiceModalState" 
            :data="order" 
            from="orders"
            action="transfer"
        ></add-edit-invoice-modal>
        <!-- create modal -->


        <!-- create Modal -->
        <add-edit-delivery-modal 
            v-if="createDeliveryModalState" 
            @modalClosed="createDeliveryModalState = false"
            modalId="create-delivery-modal-01" 
            :isModalOpen="createDeliveryModalState" 
            :data="order" 
            from="orders"
            action="transfer"
        ></add-edit-delivery-modal>
        <!-- create modal -->

    </div>

</template>

<script>
import helper from '../../helper'
import moment from 'moment'

import InvoiceComponent from '../../components/InvoiceComponent.vue'
import DeliveryComponent from '../../components/DeliveryComponent.vue'
import QuoteComponent from '../../components/QuoteComponent.vue'
import ReturnComponent from '../../components/ReturnComponent.vue'
import attachments from '../../components/Attachment.vue'
import DocumentHistory from '../../components/DocumentHistory.vue'

import AddEditInvoiceModal from '../../components/invoice/AddEditInvoiceModal'
import AddEditDeliveryModal from '../../components/delivery/AddEditDeliveryModal'

import AddEditOrderModal from '../../components/order/AddEditOrderModal.vue'
import InvoiceTemplateModal from '../../components/invoiceLayout/InvoiceTemplateModal';


export default {
  name: 'OrderDetails',
  components :{
    InvoiceComponent,
    DeliveryComponent,
    QuoteComponent,
    ReturnComponent,
    attachments,
    AddEditOrderModal,
    InvoiceTemplateModal,
    AddEditInvoiceModal,
    AddEditDeliveryModal,
    DocumentHistory
  },
  data(){
    return {
        loading : false,
        loadingHistory : false,
        order : {},
        history : [],
        historyPagination : {
            count: 0,
            per_page: 10,
            current_page: 1,
            total_pages: 1
        },
        delivery : {},
        invoice : {},
        invoices : [],
        step : 'overview',
        amount_paid : 0,
        total_purchases : 0,
        total_quote : 0,
        invoiceModalisOpen : false,
        invoiceModal : null,
        editModal : null,
        editModalisOpen : false,
        createClientState :false,
        showDilveriesModalisOpen : false,
        showCreateInvoiceModalisOpen : false,
        showInvoiceModalisOpen : false,
        showCreateDeliveryModalisOpen : false,
        showDeliveryModalisOpen : false,
        createInvoiceModalState : false,
        createDeliveryModalState : false,
        showInvoice : null,
        showDelivery : null,
        createDeliveryModal : null,
        count : 0,
        reorderMode: false,
        originalProductsOrder: null,
        orderStatusUpdatingId: null,
        orderStatusOptions: ['pending', 'confirmed', 'canceled']
    }
  },
  async created(){
    if(!this.$route.params || !this.$route.params.id) return this.$route.push('/orders')
    await this.getOrder(this.$route.params.id)
  },
  mounted(){
    window.mixpanel.track("order_details");

    //check permissions
    if(helper.checkAccess('view_orders') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_orders') == -2) return this.$router.push({path : '/subscribe'})
 
  },
    methods : {
        sanitizeRichText(value){
            return helper.sanitizeRichText(value)
        },
        ViewOrder(){
            this.invoiceModalisOpen = true;
        },
        async sharableLink() {
            const response = await helper.customPost(`${process.env.VUE_APP_API}/external-documents`, {
                document_id: this.order.id,
                document_type: 'order'
            });

            const link = response.data.link;

            try {
                await navigator.clipboard.writeText(link); // Copy to clipboard
                this.$toast.open({
                    message: this.$t('Copied'),
                    type: 'success',
                    position: 'bottom'
                });
            } catch (err) {
                this.$toast.open({
                    message: err.message,
                    type: 'error',
                    position: 'bottom'
                });
            }
        },
        async getOrder(id){
            try{
                this.loading = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/orders/${id}`)
                this.order = response.data.order
                this.total = response.data.total
                this.amount_paid = response.data.amount_paid
                this.loading = false
                     
            }catch(error){
                this.$router.push({path : '/orders'})
                this.loading = false
            }
        },
        getOrderStatusBadgeClass(status){
            return {
                'bg-info': status === 'draft',
                'bg-success': ['confirmed', 'completed'].includes(status),
                'bg-warning': status === 'partially_delivered',
                'bg-danger': status === 'canceled',
                'bg-secondary': status === 'pending'
            }
        },
        getOrderStatusOptions(order){
            return ['confirmed', 'completed', 'partially_delivered'].includes(order.status)
                ? ['canceled']
                : this.orderStatusOptions
        },
        isOrderStatusDropdownDisabled(order){
            return this.orderStatusUpdatingId === order.id
                || ['draft', 'canceled'].includes(order.status)
        },
        async changeOrderStatus(order, status){
            if (this.isOrderStatusDropdownDisabled(order) || order.status === status) return

            const previousStatus = order.status
            this.orderStatusUpdatingId = order.id
            this.$set(order, 'status', status)

            try{
                await helper.customPatch(
                    `${process.env.VUE_APP_API}/orders/${order.id}`,
                    { status },
                    { showToast: true }
                )
            }catch(error){
                this.$set(order, 'status', previousStatus)
            }finally{
                await this.getOrder(order.id)
                this.orderStatusUpdatingId = null
            }
        },
        editRow(){
            this.editModalisOpen = true;
            this.order.date = moment(this.order.date).format('YYYY-MM-DD')
        },
        async deleteRow(){
                this.$swal.fire({
                title: `${this.$t('are_you_sure')}?`,
                text : this.$t('you_cant_revert'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: this.$t('yes_delete_it')
                }).then(async (result) => {
                    if (result.isConfirmed) {
                        await helper.customDelete(`${process.env.VUE_APP_API}/orders/${this.order.id}`)
                        this.$router.push({path : '/orders'})
                    }
                })
        },
        clearForm(){
            this.order = {
                id : null,
                reference : '',
                date : '',
                products : [],
                payment_method : 'cash',
                status : 'paid',
                rest : 0,
                total : 0,
                amount_paid : 0,
            }
            // this.orderProducts = []
        },
        async getDeliveries(id){
            try{
                this.loading = true
                this.deliveries = []
                const response = await helper.customGet(`${process.env.VUE_APP_API}/deliveries?order_id=${id}`)
                this.deliveries = response.data.data
                this.loading = false
            }catch(error){
                this.loading = false
            }

        },
        async transferInvoice(){
            this.order.total = this.order.products && this.order.products.length ? this.order.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0;
            this.createInvoiceModalState = !this.createInvoiceModalState
        }, 
        async transferDelivery(){
            this.order.total = this.order.products && this.order.products.length ? this.order.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0;
            this.createDeliveryModalState = !this.createDeliveryModalState
        },
        async getOrderHistory(id){
            try{
                this.loadingHistory = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/orders/${id}/history`)
                this.history = response.data.data || response.data
                this.historyPagination = {
                    count: response.data.count || 0,
                    per_page: response.data.per_page || 10,
                    current_page: response.data.current_page || 1,
                    total_pages: response.data.total_pages || 1
                }
                this.loadingHistory = false
            }catch(error){
                alert('Error fetching history:', error)
                this.loadingHistory = false
            }
        },
        async changeHistoryPage(page){
            if(page < 1 || page > this.historyPagination.total_pages) return;

            try{
                this.loadingHistory = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/orders/${this.order.id}/history?page=${page}&per_page=${this.historyPagination.per_page}`)
                this.history = response.data.data || response.data
                this.historyPagination = {
                    count: response.data.count || 0,
                    per_page: response.data.per_page || 10,
                    current_page: response.data.current_page || 1,
                    total_pages: response.data.total_pages || 1
                }
                this.loadingHistory = false
            }catch(error){
                alert('Error fetching history:', error)
                this.loadingHistory = false
            }
        },
        formatDate(date) { return helper.formatDate(date) },
        formatMoney(number) { return helper.formatMoney(number);},
        formatReference(number,id) { return helper.formatReference(number,id);},
        toggleReorder() {
            if (!this.reorderMode) {
                // Enter reorder mode: save current order to restore if cancelled
                this.originalProductsOrder = JSON.parse(JSON.stringify(this.order.products));
                this.reorderMode = true;
            } else {
                // Cancel reorder mode: restore original order
                this.order.products = this.originalProductsOrder;
                this.reorderMode = false;
                this.originalProductsOrder = null;
            }
        },
        moveUp(index) {
            if (index > 0) {
                const products = [...this.order.products];
                const temp = products[index];
                products[index] = products[index - 1];
                products[index - 1] = temp;
                this.order.products = products;
            }
        },
        moveDown(index) {
            if (index < this.order.products.length - 1) {
                const products = [...this.order.products];
                const temp = products[index];
                products[index] = products[index + 1];
                products[index + 1] = temp;
                this.order.products = products;
            }
        },
        async saveOrder() {
            try {
                this.loading = true;
                
                const products = this.order.products.map(p => {
                    return {
                        id: p.id,
                        inventory_id: p.inventory_id,
                        product_id: p.product_id,
                        product_label: p.product_label,
                        quantity: p.quantity,
                        price: p.price,
                        unity: p.unity,
                        purchase_price: p.purchase_price,
                        vat: p.vat,
                        total: p.total,
                        sub_total: p.sub_total,
                        custom_product: p.custom_product,
                        discount: p.discount,
                        position: p.position
                    }
                })

                await helper.customPatch(`${process.env.VUE_APP_API}/orders/${this.order.id}`, {
                    reference: this.order.reference,
                    date: this.order.date,
                    client_id: this.order.client_id,
                    quote_id: this.order.quote_id,
                    notes: this.order.notes,
                    products: products
                });

                this.loading = false;
                this.reorderMode = false;
                this.originalProductsOrder = null;
                
                this.$toast.open({
                    message: this.$t('updated_successfully'),
                    type: 'success',
                    position: 'bottom'
                });
                
                // Refresh data
                this.getOrder(this.$route.params.id);
                
            } catch (error) {
                this.loading = false;
                this.$toast.open({
                    message: error.response?.data?.message || this.$t('error_occurred'),
                    type: 'error',
                    position: 'bottom'
                });
            }
        }
    },
    computed : {
        helper(){ return helper },
        business(){ return this.$store.getters.BUSINESS},
        // total(){ return this.order.products.length ? this.order.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0},
        sub_total(){ return this.order.products.length ? this.order.products.map(o => o.sub_total).reduce((a, c) => { return a + c }) : 0}
    },
    watch : {
        editModalisOpen(newValue) {
            if(newValue === false){
                this.getOrder(this.$route.params.id)
            }
        },
        step(newValue) {
            if(newValue === 'history' && this.order.id){
                this.getOrderHistory(this.order.id)
            }
        }
    }
}
</script>

<style scoped>
.product-rich-text::v-deep > :last-child {
    margin-bottom: 0 !important;
}
</style>
